import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Combobox,
  Input,
  RadioGroup,
} from "@insights/ui";
import { Check } from "lucide-react";
import { billingCountries } from "../constants/countries";
import type { BillingProfile } from "../types/billing";

const billingTypeOptions = [
  { value: "personal", label: "Personal" },
  { value: "enterprise", label: "Enterprise" },
] as const;

type BillingProfileFormProps = {
  canEdit: boolean;
  profile: BillingProfile;
  saving: boolean;
  onUpdate: (profile: BillingProfile) => Promise<BillingProfile>;
};

function getBillingProfileError(error: unknown) {
  if (!(error instanceof Error)) {
    return "Unable to update billing information";
  }

  try {
    const problem = JSON.parse(error.message) as { title?: unknown };
    if (typeof problem.title === "string") {
      return problem.title;
    }
  } catch {
    // The API client also surfaces plain-text errors.
  }

  return error.message;
}

export function BillingProfileForm({
  canEdit,
  profile,
  saving,
  onUpdate,
}: BillingProfileFormProps) {
  const [draft, setDraft] = useState(profile);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setDraft(profile);
  }, [profile]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(profile);
  const valid = Boolean(
    draft.billingEmail.trim() &&
      draft.billingName.trim() &&
      (draft.type === "personal" ||
        (draft.companyName.trim() && draft.country)),
  );

  function updateDraft(values: Partial<BillingProfile>) {
    setDraft((current) => ({ ...current, ...values }));
    setSaved(false);
    setError("");
  }

  async function submit() {
    setError("");
    setSaved(false);
    try {
      const updated = await onUpdate({
        ...draft,
        billingEmail: draft.billingEmail.trim(),
        billingName: draft.billingName.trim(),
        companyName: draft.companyName.trim(),
        vatNumber: draft.vatNumber.trim(),
      });
      setDraft(updated);
      setSaved(true);
    } catch (caught) {
      setError(getBillingProfileError(caught));
    }
  }

  return (
    <Card
      as="section"
      className="billing-profile-card"
      aria-labelledby="billing-profile-title"
    >
      <h2 className="section-card-title" id="billing-profile-title">
        Billing information
      </h2>
      <div className="billing-profile-form">
        <RadioGroup
          disabled={!canEdit || saving}
          label="Subscription type"
          name="billing-type"
          options={billingTypeOptions}
          value={draft.type}
          onValueChange={(type) => updateDraft({ type })}
        />
        {draft.type === "enterprise" && (
          <label className="billing-profile-field">
            Company name
            <Input
              disabled={!canEdit || saving}
              maxLength={200}
              required
              value={draft.companyName}
              onChange={(event) =>
                updateDraft({ companyName: event.target.value })
              }
            />
          </label>
        )}
        <label className="billing-profile-field">
          Billing email
          <Input
            disabled={!canEdit || saving}
            maxLength={320}
            required
            type="email"
            value={draft.billingEmail}
            onChange={(event) =>
              updateDraft({ billingEmail: event.target.value })
            }
          />
        </label>
        <label className="billing-profile-field">
          Billing name
          <Input
            disabled={!canEdit || saving}
            maxLength={200}
            required
            value={draft.billingName}
            onChange={(event) =>
              updateDraft({ billingName: event.target.value })
            }
          />
        </label>
        {draft.type === "enterprise" && (
          <>
            <div className="billing-profile-field">
              <span>Country</span>
              <Combobox
                disabled={!canEdit || saving}
                label="Billing country"
                options={billingCountries}
                placeholder="Select a country"
                {...(draft.country ? { value: draft.country } : {})}
                onValueChange={(country) => updateDraft({ country })}
              />
            </div>
            <label className="billing-profile-field">
              VAT number
              <Input
                disabled={!canEdit || saving}
                maxLength={64}
                value={draft.vatNumber}
                onChange={(event) =>
                  updateDraft({ vatNumber: event.target.value })
                }
              />
            </label>
          </>
        )}
      </div>
      {error && <p className="error">{error}</p>}
      {saved && <p className="billing-profile-success">Billing information updated.</p>}
      {canEdit ? (
        <div className="billing-profile-actions">
          <Button
            color="neutral"
            disabled={!dirty || saving}
            onClick={() => {
              setDraft(profile);
              setError("");
              setSaved(false);
            }}
          >
            Cancel
          </Button>
          <Button
            icon={<Check />}
            disabled={!dirty || !valid || saving}
            onClick={() => void submit()}
          >
            {saving ? "Updating…" : "Update"}
          </Button>
        </div>
      ) : (
        <p className="muted">Only workspace owners and admins can update billing information.</p>
      )}
    </Card>
  );
}
